import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { ApplicantSidebar } from './ApplicantSidebar'
import { BaseDashboardLayout } from './BaseDashboardLayout'
import {
  fetchApplicationById,
  selectApplicationData,
  selectApplications
} from '@/features/applicant/applicationSlice'
import { selectIsAuthenticated } from '@/features/auth/authSlice'
import { getStorageItem, STORAGE_KEYS } from '@/lib/utils/storage'
import { TabValidationProvider } from '@/features/applicant/context/TabValidationContext'

export function ApplicantDashboardLayout() {
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const applicationData = useAppSelector(selectApplicationData)
  const applications = useAppSelector(selectApplications)
  const hasFetchedRef = useRef(false)

  // When URL contains isImpersonate=true (e.g. from external redirect), set impersonate flag in localStorage
  useEffect(() => {
    if (searchParams.get('isImpersonate') === 'true') {
      localStorage.setItem('impersonate', 'true')
    }
  }, [searchParams])

  // Only fetch application detail by ID if we have applicant_id but not the full data
  // Don't fetch applications list here - let the dashboard component handle that
  useEffect(() => {
    if (!isAuthenticated) return

    // Prevent duplicate fetches
    if (hasFetchedRef.current) return

    // If we already have application data, no need to fetch
    if (applicationData?.applicant_id) {
      hasFetchedRef.current = true
      return
    }

    // Try to get applicant_id from storage first
    const storedApplicantId = getStorageItem(STORAGE_KEYS.applicantId)

    if (storedApplicantId) {
      // Fetch by ID directly if we have it stored
      hasFetchedRef.current = true
      dispatch(fetchApplicationById(storedApplicantId))
      return
    }

    // If we have applications list from dashboard, use the first one
    if (applications && applications.length > 0) {
      const firstApp = applications[0]
      if (firstApp?.applicant_id) {
        hasFetchedRef.current = true
        dispatch(fetchApplicationById(firstApp.applicant_id))
        return
      }
    }

    // If we're on dashboard route, wait for dashboard to fetch applications
    // Otherwise, we'll fetch when applications become available
    // This prevents duplicate calls when dashboard is already fetching
  }, [isAuthenticated, dispatch, applications]) // Only depend on applications, not applicationData

  // Reset ref when applications change (new fetch happened)
  useEffect(() => {
    if (applications && applications.length > 0 && !applicationData?.applicant_id) {
      hasFetchedRef.current = false // Allow refetch if we have new applications but no detail
    }
  }, [applications, applicationData])

  return (
    <TabValidationProvider>
      <div className="contents" data-testid="applicant-dashboard-layout">
        <BaseDashboardLayout sidebar={<ApplicantSidebar />} />
      </div>
    </TabValidationProvider>
  )
}
